import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, Lightbulb, MessageSquare } from 'lucide-react';
import { request, fmtDate, fmtNum } from '@/lib/api';
import { RefreshButton } from '@/components/RefreshButton';

const STATUSES = ['new', 'reviewed', 'resolved'] as const;
type Status = (typeof STATUSES)[number];

const STATUS_STYLE: Record<Status, string> = {
  new: 'bg-sky-100 text-sky-800',
  reviewed: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
};

const TYPE_ICON = { bug: Bug, idea: Lightbulb, other: MessageSquare } as const;

interface FeedbackRow {
  id: string;
  email: string;
  message: string;
  type: 'bug' | 'idea' | 'other';
  url: string | null;
  status: Status;
  created_at: string;
  user_full_name: string | null;
  user_email: string | null;
}

interface Summary {
  new: number;
  reviewed: number;
  resolved: number;
  total: number;
}

export function FeedbackPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<FeedbackRow[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    const qs = filter ? `?status=${filter}` : '';
    Promise.all([
      request<FeedbackRow[]>(`/backoffice/feedbacks${qs}`),
      request<Summary>('/backoffice/feedbacks/summary'),
    ])
      .then(([r, s]) => {
        setRows(r);
        setSummary(s);
      })
      .catch(() => setError(true));
  }, [filter]);

  useEffect(load, [load]);

  const setStatus = async (id: string, status: Status) => {
    try {
      await request(`/backoffice/feedbacks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      load();
    } catch {
      setError(true);
    }
  };

  if (error) return <p className="text-sm text-red-600">{t('common.error')}</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">
          {t('feedback.title')}
          {summary && (
            <span className="ml-2 text-base font-normal text-stone-400">{fmtNum(summary.total)}</span>
          )}
        </h1>
        <RefreshButton onClick={load} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('')}
          className={`rounded-full px-3 py-1.5 text-sm ${
            filter === '' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'
          }`}
        >
          {t('common.all')}
          {summary ? ` (${summary.total})` : ''}
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-sm ${
              filter === s ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'
            }`}
          >
            {t(`feedback.statuses.${s}`)}
            {summary ? ` (${summary[s]})` : ''}
          </button>
        ))}
      </div>

      {!rows ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.empty')}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((f) => {
            const TypeIcon = TYPE_ICON[f.type] ?? MessageSquare;
            const author = f.user_full_name
              ? `${f.user_full_name} <${f.user_email ?? f.email}>`
              : f.email;
            return (
              <li key={f.id} className="rounded-xl bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
                  <span className="inline-flex items-center gap-1 font-medium text-stone-700">
                    <TypeIcon size={14} />
                    {t(`feedback.types.${f.type}`)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLE[f.status]}`}
                  >
                    {t(`feedback.statuses.${f.status}`)}
                  </span>
                  <span className="truncate">{author}</span>
                  <span>· {fmtDate(f.created_at)}</span>
                  {f.url && (
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-stone-400 hover:text-amber-700 hover:underline"
                    >
                      {f.url}
                    </a>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-stone-800">{f.message}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATUSES.filter((s) => s !== f.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(f.id, s)}
                      className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50"
                    >
                      {t(`feedback.actions.${s}`)}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
